import React, { useMemo, useState } from 'react';
import styled, { css } from 'styled-components';
import { Text } from '@lmn/design-system';
import { IconArrowRightSolid } from '@/app/common/icons/libraryIcons';
import { useTranslation } from '@/app/components/translations/Translation';
import ScrollToIdLink from '@/app/components/hotel-detail/ScrollToIdLink';
import customCss from '@/app/components/hotel-detail/css/customCss';
import { decorateRatingScore, getInterval } from '@/app/components/hotel-detail/trust-you/trustyou.helper';
import { getTARatingInterval } from '@/app/components/hotel-detail/trip-advisor/tripadvisor.helper';
import TripAdvisorBubbles from '@/app/components/hotel-detail/trip-advisor/TripAdvisorBubbles';
import TripAdvisorOwl from '@/app/components/hotel-detail/trip-advisor/TripAdvisorOwl';
import TripAdvisorScore from '@/app/components/hotel-detail/trip-advisor/TripAdvisorScore';
import TripAdvisorReviewsModalContent from '@/app/components/hotel-detail/trip-advisor/TripAdvisorReviewsModalContent';
import ConditionWrapper from '@/app/components/hotel-detail/ConditionWrapper';
import TrustYouReviewsModalContent from '@/app/components/hotel-detail/trust-you/TrustYouReviewsModalContent';
import OpenXModal from '@/app/components/search/components/Modal/OpenXModal';
import BasicModalHeader from '@/app/components/search/components/Modal/LayoutElements/BasicModalHeader';
import type { Brand } from '@/domainLogic/index.ts';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { hasKnownReviewSource, isTripAdvisorSource, isTrustYouSource } from '@/app/common/reviews/reviewSource';
import { formatNumber } from '@/utils/numberUtils.ts';
import { useIsMobile } from '@/app/components/hotel-detail/hooks/useMediaSSR';
import { SubtleButton } from '@/app/components/hotel-detail/common/button';
import { keyUpHandler } from '@/app/components/hotel-detail/common/common.helper';

const RatingTitle = styled(Text).attrs({ variant: 'BodySmall', weight: 'bold' })``;

const ReviewsTitle = styled(Text).attrs({ variant: 'BodySmall', color: 'textSecondary' })``;

export const trustYouColors: { [key: string]: string } = {
  poor: '#d9d4ca',
  fair: '#f8ce72',
  good: '#bfcc4e',
  verygood: '#a0ca3f',
  excellent: '#7cb342',
};

const NoReviewsDetailsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Separator = () => <span>·</span>;

export type ReviewsSummaryProps = {
  reviewsScore: number | undefined;
  reviews?: number;
  trustYouId?: string;
  reviewSource?: string;
  showMore: 'dialog' | 'scroll' | 'none';
  onClick?: (event: React.SyntheticEvent) => void;
} & React.HTMLAttributes<HTMLElement>;

export function ReviewsSummary(props: ReviewsSummaryProps) {
  const {
    reviewsScore,
    reviews: reviewsRaw,
    trustYouId: trustYouIdRaw,
    reviewSource,
    onClick,
    showMore,
    ...rest
  } = props;
  const { t } = useTranslation();
  const [isOpen, setOpen] = useState<boolean | undefined>(undefined);
  const { staticData, localization } = usePageWrapperData();
  const isMobile = useIsMobile();

  const { locale } = localization;
  const reviews = formatNumber({ locale, number: reviewsRaw || 0 });

  const isTripAdvisor = isTripAdvisorSource(reviewSource);
  // Only a genuine TRUSTYOU source carries a real TrustYou key — a TripAdvisor hotel's
  // opinionId is not a TrustYou id and must not reach the TY modal.
  const trustYouId = isTrustYouSource(reviewSource) ? trustYouIdRaw : undefined;
  // A TrustYou hotel without its key has no reviews content to open → no link/modal chrome.
  const showMoreMode = isTripAdvisor || trustYouId ? showMore : 'none';

  const handleClick = (event: React.SyntheticEvent) => {
    onClick?.(event);
    if (showMoreMode === 'dialog') {
      setOpen(true);
    }
  };

  // Only an explicit TRUSTYOU/TRIPADVISOR source renders — any other source shows no reviews.
  if (!reviews || !reviewsScore || !hasKnownReviewSource(reviewSource)) return null;
  const ratingInterval = isTripAdvisor ? getTARatingInterval(reviewsScore) : getInterval(reviewsScore);

  return ratingInterval ? (
    <div {...rest}>
      <ConditionWrapper
        condition={showMoreMode === 'scroll'}
        // eslint-disable-next-line react/no-unstable-nested-components
        match={(children) => (
          <ScrollToIdLink
            role="button"
            targetId={'reviews'}
            className="reviews-summary-link"
            css={css`
              display: inline-flex;
              align-items: center;
              gap: 8px;
              &:hover {
                text-decoration: none;
              }
            `}
            onClick={handleClick}
          >
            {children}
          </ScrollToIdLink>
        )}
        // eslint-disable-next-line react/no-unstable-nested-components
        other={(children) => <NoReviewsDetailsContainer onClick={handleClick}>{children}</NoReviewsDetailsContainer>}
      >
        {isTripAdvisor ? (
          <>
            <TripAdvisorScore rawScore={reviewsScore ?? 0} size="inline" />
            <TripAdvisorOwl alt="TripAdvisor" />
            <TripAdvisorBubbles rawScore={reviewsScore ?? 0} css="font-size: 14px;" />
          </>
        ) : (
          <TrustYouRatingRounded rating={reviewsScore} name={ratingInterval.name} />
        )}
        {!isTripAdvisor && <RatingTitle>{t(ratingInterval.translationKey)}</RatingTitle>}
        {reviews && (
          <>
            {!isTripAdvisor && <Separator />}
            <ReviewsTitle
              css={css`
                flex-grow: 1;
                ${
                  isTripAdvisor &&
                  css`
                    color: ${({ theme }) => theme.kallisto.color.text.static.textPrimary};
                    text-decoration: underline;
                  `
                }
                @media ${customCss.breakpoints.sm} {
                  display: none;
                }
              `}
            >
              {t('vacation.select.details.product.quickinfo.reviews.number', { reviews })}
            </ReviewsTitle>
            <span
              css={css`
                display: none;
                @media ${customCss.breakpoints.sm} {
                  display: inline-block;
                }
              `}
            >
              {showMoreMode !== 'none' ? (
                <Text
                  forceTag="span"
                  variant="BodySmall"
                  color="textPrimaryDefault"
                  css={css`
                    cursor: pointer;
                    .reviews-summary-link:hover & {
                      text-decoration: underline;
                    }
                  `}
                >
                  {t('vacation.select.details.product.quickinfo.reviews.number', { reviews })}
                </Text>
              ) : (
                t('vacation.select.details.product.quickinfo.reviews.number', { reviews })
              )}
            </span>

            {!isTripAdvisor && showMoreMode !== 'none' && (
              <IconArrowRightSolid
                css={css`
                  color: ${({ theme }) => theme.commons.colors.gray800};
                  min-height: 16px;
                  min-width: 16px;
                  @media ${customCss.breakpoints.sm} {
                    display: none;
                  }
                `}
              />
            )}
          </>
        )}
      </ConditionWrapper>
      {showMoreMode !== 'none' ? (
        <OpenXModal
          isOpen={isOpen ?? false}
          isMobile={true}
          brand={staticData.domainValues?.brand as Brand}
          onClose={() => setOpen(false)}
          onCloseByBrowserHistory={() => setOpen(false)}
          onOpenByBrowserHistory={() => setOpen(true)}
          fullScreen={!isMobile}
          className={isMobile ? 'hotel-reviews-modal' : 'hotel-reviews-modal hotel-modal-lateral'}
          id="hotel-reviews-modal"
          aria-label={t('vacation.select.navigation.reviews')}
          bodyBackgroundColor="backgroundPage"
          header={{
            sticky: true,
            element: (
              <BasicModalHeader handleClose={() => setOpen(false)}>
                {t('vacation.select.navigation.reviews')}
              </BasicModalHeader>
            ),
          }}
        >
          {isTripAdvisor ? (
            <TripAdvisorReviewsModalContent />
          ) : (
            <TrustYouReviewsModalContent trustYouHotelId={trustYouId!} language={localization.lang} />
          )}
        </OpenXModal>
      ) : undefined}
    </div>
  ) : null;
}

type ReviewsSummaryRecommendedHotelCardProps = {
  reviewsScore: number;
  reviews: number;
  reviewSource?: string;
} & React.HTMLAttributes<HTMLElement>;

export function ReviewsSummaryRecommendedHotelCard(props: ReviewsSummaryRecommendedHotelCardProps) {
  const { reviewsScore, reviews: reviewsRaw, reviewSource, ...rest } = props;
  const { t } = useTranslation();
  const { localization } = usePageWrapperData();
  const { locale } = localization;
  const isTripAdvisor = isTripAdvisorSource(reviewSource);
  const ratingInterval = isTripAdvisor ? getTARatingInterval(reviewsScore) : getInterval(reviewsScore);
  const reviews = formatNumber({ locale, number: reviewsRaw || 0 });

  return ratingInterval && hasKnownReviewSource(reviewSource) ? (
    <div
      css={css`
        flex-grow: 1;
        display: inline-flex;
        align-items: center;
        gap: 8px;
      `}
      {...rest}
    >
      {isTripAdvisor ? (
        <>
          <TripAdvisorScore rawScore={reviewsScore} size="inline" />
          <TripAdvisorOwl alt="TripAdvisor" />
        </>
      ) : (
        <TrustYouRatingRounded rating={reviewsScore} name={ratingInterval.name} />
      )}
      <Text variant="BodySmall" color="textSecondary">{`${t(ratingInterval.translationKey)} (${reviews})`}</Text>
    </div>
  ) : null;
}

type ReviewsSummaryEvoProps = {
  reviewsScore: number | undefined;
  reviews: number | undefined;
  /** Opens the hotel-details modal on its Reviews tab. When omitted the summary renders as static text. */
  onOpenReviews?: () => void;
  reviewSource?: string;
} & React.HTMLAttributes<HTMLElement>;

export function ReviewsSummaryEvo(props: ReviewsSummaryEvoProps) {
  const { reviewsScore, reviews: reviewsRaw, onOpenReviews, reviewSource, ...rest } = props;
  const { t } = useTranslation();
  const { localization } = usePageWrapperData();

  const isTripAdvisor = isTripAdvisorSource(reviewSource);
  const ratingInterval = reviewsScore
    ? isTripAdvisor
      ? getTARatingInterval(reviewsScore)
      : getInterval(reviewsScore)
    : undefined;
  const { locale } = localization;
  const reviews = formatNumber({ locale, number: reviewsRaw || 0 });

  const canOpenReviews = !!onOpenReviews;

  const ReviewsContainer = useMemo(
    () =>
      canOpenReviews
        ? ({ children }: { children: React.ReactNode }) => (
            <SubtleButton
              onClick={onOpenReviews}
              onKeyUp={keyUpHandler(onOpenReviews!)}
              data-testid="reviews-summary-button"
              css={css`
                color: ${(p) => p.theme.commons.colors.gray400};
              `}
            >
              {children}
            </SubtleButton>
          )
        : ({ children }: { children: React.ReactNode }) => children,

    [canOpenReviews, onOpenReviews]
  );

  return ratingInterval && hasKnownReviewSource(reviewSource) ? (
    <div
      css={css`
        flex-grow: 1;
        display: inline-flex;
        align-items: center;
        gap: 8px;
      `}
      {...rest}
    >
      {isTripAdvisor ? (
        <>
          <TripAdvisorScore rawScore={reviewsScore ?? 0} size="inline" />
          <TripAdvisorOwl alt="TripAdvisor" />
          <TripAdvisorBubbles rawScore={reviewsScore ?? 0} css="font-size: 14px;" />
        </>
      ) : (
        <TrustYouRatingRounded rating={reviewsScore} name={ratingInterval.name} />
      )}
      <div
        css={css`
          display: flex;
          color: ${(p) => p.theme.commons.colors.gray400};
          gap: 4px;
        `}
      >
        {!isTripAdvisor && (
          <>
            <Text variant="BodySmall">{t(ratingInterval.translationKey)}</Text>
            <Text variant="BodySmall">·</Text>
          </>
        )}
        <ReviewsContainer>
          <Text variant="BodySmall">{t('vacation.select.details.product.quickinfo.reviews.number', { reviews })}</Text>
        </ReviewsContainer>
      </div>
    </div>
  ) : null;
}

export function TrustYouRatingRounded(props: { rating: number | undefined; name: string }) {
  return (
    <Text
      variant="Title6"
      color="textInverse"
      css={css`
        padding: 0 8px;
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: ${trustYouColors[props.name]};
        border-radius: 4px;
      `}
    >
      {decorateRatingScore(props.rating)}
    </Text>
  );
}
